import { useState } from 'react';
import { ExternalLink, ShieldCheck, ShieldAlert } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { format } from 'date-fns';
import { useProviderVettingStatus } from '../hooks/useProviderVettingStatus';
import { useRestartVettingCheck } from '../hooks/useVettingActions';
import { VettingStatusBadge } from './VettingStatusBadge';
import { VettingActionModal, getErrorMessage } from './VettingActionModal';
import { SendVettingCheckModal } from './SendVettingCheckModal';
import type { ProviderVettingSummary, VettingCheckSnapshot } from '../types/vetting.types';

type VettingCheckListProps = {
  providerId: string;
  /** Compact mode drops the report link / audit trail for tight card layouts (Pending tab). */
  compact?: boolean;
  /**
   * Gates the Override control. Pass useAdminAuth().canOverrideVettingCheck —
   * true only when the logged-in admin's Kinde JWT carries
   * admin:manage-user-background-vetting. Defaults to false so Override never
   * renders unless a caller opts in explicitly.
   */
  canOverride?: boolean;
  /**
   * Gates Send / Restart / Decline. Pass useAdminAuth().canManageUsersAndResources —
   * the backend requires admin:manage-users-and-resources for those endpoints.
   * Defaults to false so a view-only admin never sees actions that would 403.
   */
  canManage?: boolean;
  /**
   * The provider editor has unsaved edits. Send/Restart invite the candidate using
   * the SAVED email/name/address, so they're disabled until those edits are saved —
   * otherwise an irreversible invitation could go to the old recipient/jurisdiction.
   */
  hasUnsavedChanges?: boolean;
  /** Pre-fetched summary from a parent's batch call (e.g. Pending tab). Only used when `fromBatch` is true. */
  summary?: ProviderVettingSummary | null;
  /**
   * True when a parent supplies `summary` via useProviderVettingStatusBatch —
   * this component then never fetches (or polls) vetting-status on its own.
   */
  fromBatch?: boolean;
};

type VettingAction = 'override' | 'decline';

/**
 * Admin-facing vetting check summary. Used on both the Pending tab (compact) and
 * the provider editor's "Vetting Checks" section (full). Backed by the real
 * GET /admin/providers/:id/vetting-status endpoint — see
 * hooks/useProviderVettingStatus.ts.
 */
export function VettingCheckList({
  providerId,
  compact = false,
  canOverride = false,
  canManage = false,
  hasUnsavedChanges = false,
  summary: providedSummary,
  fromBatch = false,
}: VettingCheckListProps) {
  // Which check (and which action) the Override/Decline popup is for — a provider can have several checks.
  const [action, setAction] = useState<{ mode: VettingAction; checkId: string } | null>(null);
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  // Hook always runs (rules of hooks); disabled when a parent batch supplies the data.
  const fetchedSummary = useProviderVettingStatus(fromBatch ? undefined : providerId);
  const summary = fromBatch ? providedSummary : fetchedSummary;
  const restartCheck = useRestartVettingCheck();

  // undefined = still loading (own fetch, or parent batch not arrived yet)
  if (summary === undefined) return <VettingCheckListSkeleton compact={compact} />;
  if (!summary || summary.checks.length === 0) return null;

  return (
    <div className="space-y-2">
      {!compact && (
        <div
          className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium ${
            summary.approvable ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'
          }`}
        >
          {summary.approvable ? <ShieldCheck className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />}
          {summary.approvable
            ? 'All required vetting checks resolved — provider can be approved.'
            : summary.blockingReason}
        </div>
      )}

      {/* Every required check gets its own row and actions — an unresolved second
          check must never be hidden while it still blocks approval. */}
      {summary.checks.map((check) => (
        <VettingCheckRow
          key={check.id || check.checkType}
          check={check}
          compact={compact}
          canOverride={canOverride}
          canManage={canManage}
          hasUnsavedChanges={hasUnsavedChanges}
          isRestarting={restartCheck.isPending && restartCheck.variables?.checkId === check.id}
          restartError={
            restartCheck.isError && restartCheck.variables?.checkId === check.id
              ? getErrorMessage(restartCheck.error, 'Could not restart the check. Please try again.')
              : null
          }
          onSend={() => setConfirmSendOpen(true)}
          onRestart={() => restartCheck.mutate({ checkId: check.id, providerId })}
          onAction={(mode) => setAction({ mode, checkId: check.id })}
        />
      ))}

      {compact && !summary.approvable && (
        <p className="text-xs text-gray-500">{summary.blockingReason}</p>
      )}

      {confirmSendOpen && (
        <SendVettingCheckModal
          providerId={providerId}
          open={confirmSendOpen}
          onClose={() => setConfirmSendOpen(false)}
        />
      )}

      {action && (
        <VettingActionModal
          checkId={action.checkId}
          providerId={providerId}
          mode={action.mode}
          open
          onClose={() => setAction(null)}
        />
      )}
    </div>
  );
}

type VettingCheckRowProps = {
  check: VettingCheckSnapshot;
  compact: boolean;
  canOverride: boolean;
  canManage: boolean;
  hasUnsavedChanges: boolean;
  isRestarting: boolean;
  restartError: string | null;
  onSend: () => void;
  onRestart: () => void;
  onAction: (mode: VettingAction) => void;
};

/** One required check: label, status badge, audit details (full view) and its own actions. */
function VettingCheckRow({
  check,
  compact,
  canOverride,
  canManage,
  hasUnsavedChanges,
  isRestarting,
  restartError,
  onSend,
  onRestart,
  onAction,
}: VettingCheckRowProps) {
  const isConsiderUnresolved = check.result === 'CONSIDER' && check.resolution === 'UNRESOLVED';
  const canSend = canManage && check.status === 'NOT_STARTED';
  const canRestart =
    canManage &&
    (check.status === 'EXPIRED' ||
      check.status === 'CANCELED' ||
      check.status === 'ERROR' ||
      check.resolution === 'DECLINED');

  return (
    <div className="flex items-center justify-between rounded-md border border-gray-200 p-3">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900">{check.label}</span>
          <VettingStatusBadge status={check.status} result={check.result} resolution={check.resolution} />
        </div>
        {!compact && check.invitationSentAt && (
          <p className="text-xs text-gray-500">
            Invitation sent {format(new Date(check.invitationSentAt), 'MMM d, yyyy')}
          </p>
        )}
        {!compact && check.externalReportUrl && (
          <a
            href={check.externalReportUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-blue-700 underline"
          >
            View full report in Checkr <ExternalLink className="h-3 w-3" />
          </a>
        )}
        {!compact && check.resolution === 'ADMIN_OVERRIDE' && (
          <p className="text-xs text-gray-500">
            Overridden by {check.overriddenByAdminName}
            {check.overriddenAt && ` on ${format(new Date(check.overriddenAt), 'MMM d, yyyy')}`}
            {check.overrideNote && `: "${check.overrideNote}"`}
          </p>
        )}
        {!compact && check.resolution === 'DECLINED' && (
          <p className="text-xs text-gray-500">
            Declined by {check.declinedByAdminName}
            {check.declinedAt && ` on ${format(new Date(check.declinedAt), 'MMM d, yyyy')}`}
            {check.declineNote && `: "${check.declineNote}"`}
          </p>
        )}
        {restartError && (
          <p role="alert" className="text-xs text-red-600">
            {restartError}
          </p>
        )}
        {hasUnsavedChanges && (canSend || canRestart) && (
          <p className="text-xs text-amber-700">
            Save your changes first — the background check uses the saved email and address.
          </p>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {canSend && (
          <Button size="sm" variant="outline" disabled={hasUnsavedChanges} onClick={onSend}>
            Send Vetting Check
          </Button>
        )}
        {/* Declined checks keep blocking approval (server gate); a Restart re-vets them. */}
        {canRestart && (
          <Button size="sm" variant="outline" disabled={isRestarting || hasUnsavedChanges} onClick={onRestart}>
            {isRestarting ? 'Restarting…' : 'Restart'}
          </Button>
        )}
        {!compact && isConsiderUnresolved && (
          <>
            {canOverride && (
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={() => onAction('override')}>
                Override
              </Button>
            )}
            {canManage && (
              <Button size="sm" variant="destructive" onClick={() => onAction('decline')}>
                Decline
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** Placeholder shaped like the loaded list: status banner (full view only) + one check row. */
function VettingCheckListSkeleton({ compact }: { compact: boolean }) {
  return (
    <div className="space-y-2" role="status" aria-label="Loading vetting status">
      {!compact && <div className="h-9 w-full animate-pulse rounded-md bg-gray-100" />}
      <div className="flex items-center justify-between rounded-md border border-gray-200 p-3">
        <div className="flex items-center gap-2">
          <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
          <div className="h-5 w-20 animate-pulse rounded-full bg-gray-100" />
        </div>
        <div className="h-8 w-36 animate-pulse rounded-md bg-gray-100" />
      </div>
    </div>
  );
}
